import Box from "@mui/material/Box";
import {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationResourceListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {CommonAggregationService} from "crqlar-react-common/dist/services/aggregation/commonAggregation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {AggregationResourceElement} from "../../common/uiElements/ResourceElement";
import SettingsListPage from "../layout/SettingsListPage";

const ResourcesList = () => {
    useTrackScreenVisit('system resources');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const columns = useMemo<ColumnDef<AggregationResourceListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: resource => resource.name,
            cell: ({row}) => <AggregationResourceElement resource={row.original}/>,
            size: 240,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                grow: true,
                printable: true,
                printDefault: true,
                printCell: row => <AggregationResourceElement resource={row.original} asPrint/>
            }
        },
        {
            id: 'categories',
            header: t('Categories'),
            accessorFn: resource => resource.categories.map(category => category.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.categories.map(category => category.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 220,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'sectors',
            header: t('Sectors'),
            accessorFn: resource => resource.sectors.map(sector => sector.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.sectors.map(sector => sector.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'sectorName',
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'managedExternally',
            header: t('Managed externally'),
            accessorFn: resource => resource.managedExternally,
            cell: ({row}) => <Box>{row.original.managedExternally ? t('Yes') : t('No')}</Box>,
            size: 180,
            meta: {defaultActive: false}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: resource => resource.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'capacity',
            header: t('Capacity'),
            accessorFn: resource => resource.capacity,
            cell: ({row}) => <Box>{row.original.capacity ?? ''}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: resource => resource.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true
            }
        }
    ], [t]);

    const filterOptionsQuery = useQuery(
        ['server-table-resource-filter-options'],
        () => ResourceService.getFilterOptions(),
        {staleTime: Infinity}
    );

    const controller = useServerTable({
        tableId: 'system-resources',
        columns,
        getRowId: resource => resource.id,
        onRowClick: resource => navigate(`/system/resources/${resource.id}`),
        initialSorting: [{id: 'sectors', desc: false}],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => CommonAggregationService.listResources({
            onlyRootResources: true,
            name: searchQuery ? [searchQuery] : undefined,
            advancedFilter: filterRules.length ? {
                filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
            } : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    return (
        <SettingsListPage title={t('Resources')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFilter
                            options={filterOptionsQuery.data ?? []}
                            disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                        />
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>

                        <ServerTableCreate
                            label={t('Create resource')}
                            onClick={() => navigate('/system/resources/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ResourcesList;
